/**
 * Phase 5 — Multi-notation learning modes.
 * Labels/overlays stay data-driven so every lesson/platform stays in sync.
 */

import { clampMidiNote, midiToNoteName } from "../audio/midiMath.js";

export type NotationMode =
  | "letter" // C, D, E…
  | "solfege" // Do Re Mi… (movable Do simplified as fixed C=Do for C-major drills)
  | "indian" // Sa Re Ga Ma Pa Dha Ni
  | "finger" // 1–5 when finger provided, else pitch-class hint
  | "staff"; // Traditional staff (rendered separately)

export const NOTATION_MODES: Array<{ id: NotationMode; label: string }> = [
  { id: "letter", label: "Letters (C D E…)" },
  { id: "solfege", label: "Solfège (Do Re Mi…)" },
  { id: "indian", label: "Indian (Sa Re Ga…)" },
  { id: "finger", label: "Finger numbers (1–5)" },
  { id: "staff", label: "Western staff" },
];

const LETTER = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const SOLFEGE = ["Do", "Di", "Re", "Ri", "Mi", "Fa", "Fi", "Sol", "Si", "La", "Li", "Ti"];
/** Shuddha scale names; chromatic use komal/tivra markers lightly. */
const INDIAN = ["Sa", "re", "Re", "ga", "Ga", "Ma", "Ma#", "Pa", "dha", "Dha", "ni", "Ni"];

export interface NotationLabelOptions {
  /** Finger 1–5 if known from lesson data. */
  finger?: 1 | 2 | 3 | 4 | 5;
  /** Include octave digit for letter mode (C4). */
  withOctave?: boolean;
}

/** Short label drawn on keys / note lists. */
export function labelForMidi(
  midiNote: number,
  mode: NotationMode,
  options: NotationLabelOptions = {},
): string {
  const n = clampMidiNote(midiNote);
  const pc = n % 12;
  const { octave } = midiToNoteName(n);

  switch (mode) {
    case "letter":
      return options.withOctave === false ? LETTER[pc] : `${LETTER[pc]}${octave}`;
    case "solfege":
      return SOLFEGE[pc];
    case "indian":
      return INDIAN[pc];
    case "finger":
      return options.finger ? String(options.finger) : "·";
    case "staff":
      // Staff mode uses canvas; fallback letter for lists.
      return `${LETTER[pc]}${octave}`;
    default:
      return LETTER[pc];
  }
}

/**
 * Staff position for treble (G2 = bottom ledger-ish math).
 * Returns diatonic steps relative to Middle C = 0 on "staff line math"
 * where each step is a staff position (line/space).
 */
export function staffStepsFromC4(midiNote: number): number {
  // Map pitch classes to diatonic degree ignoring accidentals for position,
  // then adjust octave. Sharps/flats share the natural's staff position.
  const n = clampMidiNote(midiNote);
  const NATURAL_DEGREE = [0, 0, 1, 1, 2, 3, 3, 4, 4, 5, 5, 6]; // C C# D…
  const pc = n % 12;
  const octave = Math.floor(n / 12) - 1;
  const c4Octave = 4;
  const deg = NATURAL_DEGREE[pc];
  return deg + (octave - c4Octave) * 7;
}

export function isAccidental(midiNote: number): boolean {
  const pc = clampMidiNote(midiNote) % 12;
  return pc === 1 || pc === 3 || pc === 6 || pc === 8 || pc === 10;
}

/** Suggested default finger for C-position white keys (RH middle C row). */
export function defaultCPositionFinger(midiNote: number): 1 | 2 | 3 | 4 | 5 | undefined {
  return fingerForCPosition(midiNote, "right");
}

/** C-position five-finger map for RH (C4–G4) or LH (C3–G3). */
export function fingerForCPosition(
  midiNote: number,
  hand: "right" | "left" | "both" = "right",
): 1 | 2 | 3 | 4 | 5 | undefined {
  const midi = clampMidiNote(midiNote);
  if (hand === "left") {
    const lh: Record<number, 1 | 2 | 3 | 4 | 5> = {
      48: 5,
      50: 4,
      52: 3,
      53: 2,
      55: 1,
    };
    return lh[midi];
  }
  const rh: Record<number, 1 | 2 | 3 | 4 | 5> = {
    60: 1,
    62: 2,
    64: 3,
    65: 4,
    67: 5,
  };
  return rh[midi] ?? (hand === "both" ? fingerForCPosition(midi, "left") : undefined);
}
